/**
 * QUIPWA — SyncEngine
 *
 * The one place that decides *when* to talk to Drive, and in what order.
 * Everything else writes locally and, at most, asks for a sync.
 *
 *   run():  state ─► outbox ─► decks ─► notes ─► (state again if needed)
 *
 * When it runs:
 *   - on start and whenever the session becomes usable
 *   - when the network comes back (`online`)
 *   - when the app becomes visible again, if the last sync is > 1 min old
 *   - every 5 min while visible
 *   - 20 s after the last local change (debounced), or at once when a caller
 *     asks with `{ urgent: true }` (end of a study session, "Sincronizar")
 *
 * Guarantees:
 *   - single flight: one run at a time; a request during a run schedules
 *     exactly one more run afterwards
 *   - automatic retries back off (30 s → 10 min); a manual sync always runs
 *   - status is published to `useSyncStatus` for the UI
 *
 * See docs/modulos/sincronizacion.md.
 */

import { isDriveError } from '../drive/drive-errors.ts'
import type { Outbox, OutboxOp } from '../local/outbox.ts'
import { outboxBackoffMs } from '../local/outbox.ts'
import type { KvStore } from '../local/kv-store.ts'
import { KV } from '../local/kv-store.ts'
import type { StateReplicator, StateSyncReport } from './state-replicator.ts'
import type { DeckSync, DeckSyncReport } from './deck-sync.ts'
import type { SyncPhase, SyncState, SyncStatus } from './sync-status-store.ts'

export type AuthAvailability = 'active' | 'expired' | 'needs_consent' | 'signed_out'

/**
 * The status to show when a sync cannot run (no usable session, no network),
 * or null when it can. Also used for the status before the engine's first run,
 * so the UI never claims "Sincronizado" for a signed-out device.
 */
export function blockedStatus(auth: AuthAvailability, online: boolean): SyncStatus | null {
  if (auth === 'signed_out') return 'signed_out'
  if (auth === 'needs_consent') return 'needs_consent'
  if (auth === 'expired') return 'needs_auth'
  if (!online) return 'offline'
  return null
}

export type OutboxHandler = (op: OutboxOp) => Promise<void>

/** Thrown by a handler when an op can never succeed and should be dropped. */
export class DropOutboxOp extends Error {}

export interface NotesPhase {
  /** Pull notes from Drive. `changed` counts notes added, updated or removed locally. */
  sync(): Promise<{ changed: number }>
}

export interface SyncReport {
  state: StateSyncReport | null
  decks: DeckSyncReport | null
  notes: { changed: number } | null
  outboxProcessed: number
}

export interface SyncEngineDeps {
  state: StateReplicator
  decks: DeckSync
  outbox: Outbox
  kv: KvStore
  handlers: Record<string, OutboxHandler>
  notes?: NotesPhase
  auth: () => AuthAvailability
  setStatus: (patch: Partial<Omit<SyncState, 'set'>>) => void
  /** Called after any run that changed local data (UI caches should refresh). */
  onLocalDataChanged?: (report: SyncReport) => void
  isOnline?: () => boolean
  now?: () => number
  timers?: Pick<typeof globalThis, 'setTimeout' | 'clearTimeout' | 'setInterval' | 'clearInterval'>
  events?: {
    window?: Pick<Window, 'addEventListener' | 'removeEventListener'> | null
    document?: Pick<Document, 'addEventListener' | 'removeEventListener' | 'visibilityState'> | null
  }
}

const LOCAL_CHANGE_DEBOUNCE_MS = 20_000
const PERIODIC_MS = 5 * 60_000
const STALE_ON_FOCUS_MS = 60_000
const MAX_OUTBOX_BATCHES = 20

export function autoRetryDelayMs(consecutiveFailures: number): number {
  return Math.min(10 * 60_000, 30_000 * 2 ** Math.max(0, consecutiveFailures - 1))
}

export class SyncEngine {
  private readonly deps: SyncEngineDeps
  private readonly timers: NonNullable<SyncEngineDeps['timers']>
  private readonly now: () => number
  private readonly isOnline: () => boolean

  private running: Promise<SyncReport> | null = null
  private rerun = false
  private debounceTimer: ReturnType<typeof setTimeout> | null = null
  private retryTimer: ReturnType<typeof setTimeout> | null = null
  private periodicTimer: ReturnType<typeof setInterval> | null = null
  private consecutiveFailures = 0
  private lastSuccessAt = 0
  private started = false

  constructor(deps: SyncEngineDeps) {
    this.deps = deps
    this.timers = deps.timers ?? globalThis
    this.now = deps.now ?? Date.now
    this.isOnline = deps.isOnline ?? (() => typeof navigator === 'undefined' || navigator.onLine !== false)
  }

  // ── lifecycle ─────────────────────────────────────────────────────────────

  start(): void {
    if (this.started) return
    this.started = true
    const win = this.deps.events?.window !== undefined ? this.deps.events.window : typeof window === 'undefined' ? null : window
    const doc = this.deps.events?.document !== undefined ? this.deps.events.document : typeof document === 'undefined' ? null : document
    win?.addEventListener('online', this.onOnline)
    win?.addEventListener('offline', this.onOffline)
    doc?.addEventListener('visibilitychange', this.onVisibility)
    this.periodicTimer = this.timers.setInterval(() => {
      if (!doc || doc.visibilityState === 'visible') this.requestSync('periodic')
    }, PERIODIC_MS)
    void this.refreshOutboxCounts()
    this.deps.outbox.onChange(() => void this.refreshOutboxCounts())
    this.requestSync('start', { urgent: true })
  }

  stop(): void {
    this.started = false
    const win = this.deps.events?.window !== undefined ? this.deps.events.window : typeof window === 'undefined' ? null : window
    const doc = this.deps.events?.document !== undefined ? this.deps.events.document : typeof document === 'undefined' ? null : document
    win?.removeEventListener('online', this.onOnline)
    win?.removeEventListener('offline', this.onOffline)
    doc?.removeEventListener('visibilitychange', this.onVisibility)
    if (this.periodicTimer !== null) this.timers.clearInterval(this.periodicTimer)
    this.clearTimer('debounce')
    this.clearTimer('retry')
  }

  // ── public API ────────────────────────────────────────────────────────────

  /**
   * Ask for a sync. Non-urgent requests are debounced (local edits arrive in
   * bursts); urgent ones run now. Never throws.
   */
  requestSync(reason: string, options: { urgent?: boolean } = {}): void {
    if (options.urgent) {
      this.clearTimer('debounce')
      void this.run(reason).catch(() => undefined)
      return
    }
    if (this.debounceTimer !== null) return
    this.debounceTimer = this.timers.setTimeout(() => {
      this.debounceTimer = null
      void this.run(reason).catch(() => undefined)
    }, LOCAL_CHANGE_DEBOUNCE_MS)
  }

  /** "Sincronizar ahora": runs immediately, ignores backoff, retries parked ops. Resolves with the report. */
  async syncNow(): Promise<SyncReport> {
    await this.deps.outbox.retryAll()
    this.consecutiveFailures = 0
    this.clearTimer('retry')
    return this.run('manual', { rethrow: true })
  }

  get isRunning(): boolean {
    return this.running !== null
  }

  // ── the run ───────────────────────────────────────────────────────────────

  private async run(reason: string, options: { rethrow?: boolean } = {}): Promise<SyncReport> {
    if (this.running) {
      this.rerun = true
      return this.running
    }
    this.running = this.execute(reason)
    try {
      return await this.running
    } catch (err) {
      if (options.rethrow) throw err
      return { state: null, decks: null, notes: null, outboxProcessed: 0 }
    } finally {
      this.running = null
      if (this.rerun) {
        this.rerun = false
        void this.run('rerun').catch(() => undefined)
      }
    }
  }

  private async execute(reason: string): Promise<SyncReport> {
    const blocked = blockedStatus(this.deps.auth(), this.isOnline())
    if (blocked) {
      this.publish(blocked)
      throw new Error(`sync skipped (${reason}): ${blocked}`)
    }

    const report: SyncReport = { state: null, decks: null, notes: null, outboxProcessed: 0 }
    this.setStatus({ status: 'syncing', lastError: null })
    try {
      report.state = await this.phase('state', () => this.deps.state.sync())
      this.setStatus({ appDataUnavailable: report.state.appDataUnavailable })
      report.outboxProcessed = await this.phase('outbox', () => this.drainOutbox())
      report.decks = await this.phase('decks', () => this.deps.decks.run())
      if (this.deps.notes) report.notes = await this.phase('notes', () => this.deps.notes!.sync())
      // Outbox work (e.g. a deck upload binding its file id) can dirty the state again.
      if (await this.deps.state.hasUnpushedChanges()) {
        await this.phase('state', () => this.deps.state.sync())
      }

      this.consecutiveFailures = 0
      this.lastSuccessAt = this.now()
      await this.deps.kv.set(KV.lastSyncedAt, this.lastSuccessAt)
      const { pending, parked } = await this.deps.outbox.count()
      const dirty = await this.deps.state.hasUnpushedChanges()
      this.setStatus({
        status: pending + parked > 0 || dirty ? 'pending' : 'synced',
        phase: 'idle',
        lastSyncedAt: this.lastSuccessAt,
        lastError: null,
        pendingOps: pending,
        parkedOps: parked,
      })

      const changedLocally =
        (report.state?.localChanges ?? 0) > 0 ||
        (report.decks ? report.decks.hydrated + report.decks.updated + report.decks.imported > 0 : false) ||
        (report.notes?.changed ?? 0) > 0 ||
        report.outboxProcessed > 0
      if (changedLocally) this.deps.onLocalDataChanged?.(report)
      return report
    } catch (err) {
      this.setStatus({ phase: 'idle' })
      this.handleFailure(err)
      throw err
    }
  }

  private async phase<T>(phase: SyncPhase, fn: () => Promise<T>): Promise<T> {
    this.setStatus({ phase })
    return fn()
  }

  /** Send queued writes. Stops the whole run on offline/auth errors; backs off per op otherwise. */
  private async drainOutbox(): Promise<number> {
    let processed = 0
    for (let batch = 0; batch < MAX_OUTBOX_BATCHES; batch++) {
      const ops = await this.deps.outbox.due(25)
      if (ops.length === 0) break
      for (const op of ops) {
        const handler = this.deps.handlers[op.kind]
        if (!handler) {
          await this.deps.outbox.fail(op, `Sin manejador para "${op.kind}"`)
          continue
        }
        try {
          await handler(op)
          await this.deps.outbox.complete(op)
          processed += 1
        } catch (err) {
          if (err instanceof DropOutboxOp) {
            await this.deps.outbox.complete(op)
            continue
          }
          if (isDriveError(err, 'offline', 'unauthorized', 'insufficient_scope')) throw err
          const retryIn = isDriveError(err) && err.retryAfterSeconds !== null
            ? err.retryAfterSeconds * 1000
            : outboxBackoffMs(op.attempts + 1)
          await this.deps.outbox.fail(op, err instanceof Error ? err.message : String(err), retryIn)
        }
      }
    }
    return processed
  }

  // ── status & retries ──────────────────────────────────────────────────────

  private handleFailure(err: unknown): void {
    if (isDriveError(err, 'offline')) {
      this.publish('offline')
      return // `online` will trigger the next run
    }
    if (isDriveError(err, 'unauthorized')) {
      this.publish('needs_auth')
      return // reconnecting will trigger the next run
    }
    if (isDriveError(err, 'insufficient_scope')) {
      this.publish('needs_consent')
      return // granting the permission will trigger the next run
    }
    this.consecutiveFailures += 1
    this.setStatus({ status: 'error', lastError: err instanceof Error ? err.message : String(err) })
    this.clearTimer('retry')
    this.retryTimer = this.timers.setTimeout(() => {
      this.retryTimer = null
      this.requestSync('retry', { urgent: true })
    }, autoRetryDelayMs(this.consecutiveFailures))
  }

  private publish(status: SyncStatus): void {
    this.setStatus({ status, phase: 'idle' })
  }

  private async refreshOutboxCounts(): Promise<void> {
    const { pending, parked } = await this.deps.outbox.count()
    const dirty = await this.deps.state.hasUnpushedChanges()
    this.setStatus({ pendingOps: pending, parkedOps: parked })
    // Only move between "synced" and "pending"; never mask error/offline/auth.
    if (!this.running && (this.currentStatus === 'synced' || this.currentStatus === 'pending')) {
      this.publish(pending + parked > 0 || dirty ? 'pending' : 'synced')
    }
  }

  /** Last status published, so background refreshes don't overwrite an error. */
  private currentStatus: SyncStatus = 'synced'

  private setStatus(patch: Partial<Omit<SyncState, 'set'>>): void {
    if (patch.status) this.currentStatus = patch.status
    this.deps.setStatus(patch)
  }

  private readonly onOnline = () => this.requestSync('online', { urgent: true })
  private readonly onOffline = () => this.publish('offline')
  private readonly onVisibility = () => {
    const doc = this.deps.events?.document ?? (typeof document === 'undefined' ? null : document)
    if (doc?.visibilityState === 'visible' && this.now() - this.lastSuccessAt > STALE_ON_FOCUS_MS) {
      this.requestSync('visible', { urgent: true })
    }
  }

  private clearTimer(which: 'debounce' | 'retry'): void {
    if (which === 'debounce' && this.debounceTimer !== null) {
      this.timers.clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
    if (which === 'retry' && this.retryTimer !== null) {
      this.timers.clearTimeout(this.retryTimer)
      this.retryTimer = null
    }
  }
}


